/**
 * Browser-side Firebase init.
 *
 * Loads the Firebase config from `/__/firebase/init.json`, which Firebase
 * Hosting auto-serves with the correct project credentials (no .env needed).
 * Falls back to Vite env vars (VITE_FIREBASE_*) for local dev outside the
 * `firebase emulators` host.
 */
import { initializeApp, FirebaseApp, FirebaseOptions } from 'firebase/app';
import { getAuth, Auth, signInAnonymously, onAuthStateChanged, User } from 'firebase/auth';
import { getDatabase, Database } from 'firebase/database';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';

// Silence a noisy "heartbeats undefined" warning emitted by the Firebase JS
// SDK's internal HeartbeatService when it can't read prior heartbeats from
// IndexedDB (private windows, Safari, first load, etc.). It's harmless and
// not actionable by us. See firebase-js-sdk#8061.
const _origWarn = console.warn.bind(console);
console.warn = (...args: any[]) => {
	const first = args[0];
	if (typeof first === 'string' && /heartbeats? (?:is )?undefined/i.test(first)) return;
	_origWarn(...args);
};

async function loadConfig(): Promise<FirebaseOptions> {
	try {
		const r = await fetch('/__/firebase/init.json', { cache: 'no-cache' });
		if (r.ok) return (await r.json()) as FirebaseOptions;
	} catch {
		/* fall through to env */
	}
	return {
		apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
		authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
		projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
		databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
		storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
		appId: import.meta.env.VITE_FIREBASE_APP_ID
	};
}

const FIREBASE_CONFIG = await loadConfig();

export const firebaseApp: FirebaseApp = initializeApp(FIREBASE_CONFIG);
export const firebaseAuth: Auth = getAuth(firebaseApp);
export const rtdb: Database = getDatabase(firebaseApp);
export const firestore: Firestore = getFirestore(firebaseApp);
export const storage: FirebaseStorage = getStorage(firebaseApp);

// Ensure we always have an authenticated user (anonymous if no real login)
// so RTDB rules requiring `auth != null` accept reads/writes.
export const authReady: Promise<User> = new Promise((resolve, reject) => {
	const unsub = onAuthStateChanged(firebaseAuth, async (u) => {
		if (u) {
			unsub();
			resolve(u);
			return;
		}
		try {
			await signInAnonymously(firebaseAuth);
		} catch (e) {
			unsub();
			reject(e);
		}
	});
});
